'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { POPULAR_LOCATIONS, LocationPreset } from '@/lib/location/resolver';
import { LocationAutocomplete } from '@/components/quick-calculator/LocationAutocomplete';
import { calculateKundliMatching } from '@/lib/api/client';
import { Calendar, Clock, User, AlertCircle, Loader2, Lock, HeartHandshake } from 'lucide-react';

interface TwoPersonReportOrderFormProps {
  locale: string;
  labels: {
    formTitle: string;
    formSubtitle: string;
    partnerALabel: string;
    partnerBLabel: string;
    nameLabel: string;
    namePlaceholderA: string;
    namePlaceholderB: string;
    dateLabel: string;
    timeLabel: string;
    placeLabel: string;
    placePlaceholder: string;
    seeFreeMatchScore: string;
    orderFullDisabled: string;
    privacyNotice: string;
  };
}

export function TwoPersonReportOrderForm({ locale, labels }: TwoPersonReportOrderFormProps) {
  const router = useRouter();
  const isHi = locale === 'hi';

  const localizeHref = (path: string) => {
    if (!isHi) return path;
    return path === '/' ? '/hi' : `/hi${path}`;
  };

  // Partner A state (defaults to New Delhi sample chart values)
  const [nameA, setNameA] = useState('');
  const [birthDateA, setBirthDateA] = useState('1990-01-01');
  const [birthTimeA, setBirthTimeA] = useState('12:00:00');
  const [locationNameA, setLocationNameA] = useState('New Delhi, Delhi, India');
  const [presetA, setPresetA] = useState<LocationPreset | null>(POPULAR_LOCATIONS[0]);

  // Partner B state (defaults to Mumbai candidate values)
  const [nameB, setNameB] = useState('');
  const [birthDateB, setBirthDateB] = useState('1992-05-15');
  const [birthTimeB, setBirthTimeB] = useState('15:30:00');
  const [locationNameB, setLocationNameB] = useState('Mumbai, Maharashtra, India');
  const [presetB, setPresetB] = useState<LocationPreset | null>(POPULAR_LOCATIONS[1] || POPULAR_LOCATIONS[0]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedA = nameA.trim();
    const trimmedB = nameB.trim();

    if (!trimmedA) {
      setError(isHi ? 'कृपया प्रथम साथी का पूरा नाम दर्ज करें।' : 'Please enter the first partner full name.');
      return;
    }
    if (!birthDateA || !birthTimeA || !presetA) {
      setError(isHi ? 'कृपया प्रथम साथी का पूर्ण जन्म विवरण प्रदान करें।' : 'Please enter complete birth details for the first partner.');
      return;
    }

    if (!trimmedB) {
      setError(isHi ? 'कृपया द्वितीय साथी का पूरा नाम दर्ज करें।' : 'Please enter the second partner full name.');
      return;
    }
    if (!birthDateB || !birthTimeB || !presetB) {
      setError(isHi ? 'कृपया द्वितीय साथी का पूर्ण जन्म विवरण प्रदान करें।' : 'Please enter complete birth details for the second partner.');
      return;
    }

    try {
      setLoading(true);
      const normalizedTimeA = birthTimeA.length === 5 ? `${birthTimeA}:00` : birthTimeA;
      const normalizedTimeB = birthTimeB.length === 5 ? `${birthTimeB}:00` : birthTimeB;

      const personAInput = {
        name: trimmedA,
        birthDate: birthDateA,
        birthTime: normalizedTimeA,
        latitude: presetA.latitude,
        longitude: presetA.longitude,
        timezone: presetA.timezone,
        locationName: presetA.name,
      };

      const personBInput = {
        name: trimmedB,
        birthDate: birthDateB,
        birthTime: normalizedTimeB,
        latitude: presetB.latitude,
        longitude: presetB.longitude,
        timezone: presetB.timezone,
        locationName: presetB.name,
      };

      try {
        await calculateKundliMatching({ personA: personAInput, personB: personBInput });
      } catch {
        // Proceed into kundli-matching route even if transient api has network issue
      }

      router.push(localizeHref('/kundli-matching'));
    } catch (err: any) {
      setError(err.message || (isHi ? 'गणना के दौरान त्रुटि हुई।' : 'An error occurred during calculation.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="bg-[rgba(5,10,36,0.95)] border border-white/20 rounded-3xl p-6 sm:p-8 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl relative"
      data-testid="two-person-order-form-container"
    >
      <div className="mb-6">
        <h3 className="text-xl sm:text-2xl font-['Lexend'] font-semibold text-white tracking-tight">
          {labels.formTitle}
        </h3>
        <p className="text-xs sm:text-sm text-[#dcecff]/75 font-['Outfit'] mt-1 leading-relaxed">
          {labels.formSubtitle}
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-xl bg-rose-500/10 border border-rose-500/30 flex items-start gap-3 text-rose-200 text-xs font-['Outfit']">
          <AlertCircle className="w-4 h-4 shrink-0 text-rose-400 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Partner 1 (Person A / First Partner) */}
          <div className="p-4 sm:p-5 rounded-2xl bg-white/[0.03] border border-[#5ab4ff]/25 space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-white/10">
              <span className="w-2 h-2 rounded-full bg-[#5ab4ff]" />
              <h4 className="text-xs sm:text-sm font-['Lexend'] font-semibold text-white">
                {labels.partnerALabel}
              </h4>
            </div>

            {/* Name */}
            <div className="space-y-1.5">
              <label htmlFor="partner-a-name" className="text-xs font-medium text-[#dcecff]/90 flex items-center gap-1.5 font-['Outfit']">
                <User className="w-3.5 h-3.5 text-[#5ab4ff]" />
                <span>{labels.nameLabel}</span>
              </label>
              <input
                id="partner-a-name"
                type="text"
                value={nameA}
                onChange={(e) => setNameA(e.target.value)}
                placeholder={labels.namePlaceholderA}
                className="w-full h-11 px-3.5 rounded-xl bg-white/[0.06] border border-white/16 text-white text-xs placeholder:text-[#dcecff]/40 focus:outline-none focus:border-[#5ab4ff] focus:ring-1 focus:ring-[#5ab4ff] transition-all font-['Outfit']"
                required
              />
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label htmlFor="partner-a-date" className="text-xs font-medium text-[#dcecff]/90 flex items-center gap-1.5 font-['Outfit']">
                  <Calendar className="w-3.5 h-3.5 text-[#5ab4ff]" />
                  <span>{labels.dateLabel}</span>
                </label>
                <input
                  id="partner-a-date"
                  type="date"
                  value={birthDateA}
                  onChange={(e) => setBirthDateA(e.target.value)}
                  className="w-full h-11 px-3 rounded-xl bg-white/[0.06] border border-white/16 text-white text-xs focus:outline-none focus:border-[#5ab4ff] focus:ring-1 focus:ring-[#5ab4ff] transition-all font-['Outfit'] [color-scheme:dark]"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="partner-a-time" className="text-xs font-medium text-[#dcecff]/90 flex items-center gap-1.5 font-['Outfit']">
                  <Clock className="w-3.5 h-3.5 text-[#5ab4ff]" />
                  <span>{labels.timeLabel}</span>
                </label>
                <input
                  id="partner-a-time"
                  type="time"
                  step="1"
                  value={birthTimeA}
                  onChange={(e) => setBirthTimeA(e.target.value)}
                  className="w-full h-11 px-3 rounded-xl bg-white/[0.06] border border-white/16 text-white text-xs focus:outline-none focus:border-[#5ab4ff] focus:ring-1 focus:ring-[#5ab4ff] transition-all font-['Outfit'] [color-scheme:dark]"
                  required
                />
              </div>
            </div>

            {/* Location */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#dcecff]/90 block font-['Outfit']">
                {labels.placeLabel}
              </label>
              <LocationAutocomplete
                value={locationNameA}
                onSelectLocation={(preset) => {
                  setPresetA(preset);
                  setLocationNameA(preset.name);
                }}
                onClearLocation={() => setPresetA(null)}
                confirmedPreset={presetA}
                idPrefix="partner-a-report-loc"
              />
            </div>
          </div>

          {/* Partner 2 (Person B / Second Partner) */}
          <div className="p-4 sm:p-5 rounded-2xl bg-white/[0.03] border border-[#ff9bb0]/25 space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-white/10">
              <span className="w-2 h-2 rounded-full bg-[#ff9bb0]" />
              <h4 className="text-xs sm:text-sm font-['Lexend'] font-semibold text-white">
                {labels.partnerBLabel}
              </h4>
            </div>

            {/* Name */}
            <div className="space-y-1.5">
              <label htmlFor="partner-b-name" className="text-xs font-medium text-[#dcecff]/90 flex items-center gap-1.5 font-['Outfit']">
                <User className="w-3.5 h-3.5 text-[#ff9bb0]" />
                <span>{labels.nameLabel}</span>
              </label>
              <input
                id="partner-b-name"
                type="text"
                value={nameB}
                onChange={(e) => setNameB(e.target.value)}
                placeholder={labels.namePlaceholderB}
                className="w-full h-11 px-3.5 rounded-xl bg-white/[0.06] border border-white/16 text-white text-xs placeholder:text-[#dcecff]/40 focus:outline-none focus:border-[#ff9bb0] focus:ring-1 focus:ring-[#ff9bb0] transition-all font-['Outfit']"
                required
              />
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label htmlFor="partner-b-date" className="text-xs font-medium text-[#dcecff]/90 flex items-center gap-1.5 font-['Outfit']">
                  <Calendar className="w-3.5 h-3.5 text-[#ff9bb0]" />
                  <span>{labels.dateLabel}</span>
                </label>
                <input
                  id="partner-b-date"
                  type="date"
                  value={birthDateB}
                  onChange={(e) => setBirthDateB(e.target.value)}
                  className="w-full h-11 px-3 rounded-xl bg-white/[0.06] border border-white/16 text-white text-xs focus:outline-none focus:border-[#ff9bb0] focus:ring-1 focus:ring-[#ff9bb0] transition-all font-['Outfit'] [color-scheme:dark]"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="partner-b-time" className="text-xs font-medium text-[#dcecff]/90 flex items-center gap-1.5 font-['Outfit']">
                  <Clock className="w-3.5 h-3.5 text-[#ff9bb0]" />
                  <span>{labels.timeLabel}</span>
                </label>
                <input
                  id="partner-b-time"
                  type="time"
                  step="1"
                  value={birthTimeB}
                  onChange={(e) => setBirthTimeB(e.target.value)}
                  className="w-full h-11 px-3 rounded-xl bg-white/[0.06] border border-white/16 text-white text-xs focus:outline-none focus:border-[#ff9bb0] focus:ring-1 focus:ring-[#ff9bb0] transition-all font-['Outfit'] [color-scheme:dark]"
                  required
                />
              </div>
            </div>

            {/* Location */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#dcecff]/90 block font-['Outfit']">
                {labels.placeLabel}
              </label>
              <LocationAutocomplete
                value={locationNameB}
                onSelectLocation={(preset) => {
                  setPresetB(preset);
                  setLocationNameB(preset.name);
                }}
                onClearLocation={() => setPresetB(null)}
                confirmedPreset={presetB}
                idPrefix="partner-b-report-loc"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 pt-2">
          {/* Primary Submit Button: Free Match Score */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 px-6 rounded-full bg-white text-[#03163d] font-semibold text-xs sm:text-sm hover:bg-white/90 shadow-[0_0_25px_rgba(255,155,176,0.35)] transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-[0.99] disabled:opacity-50"
            data-testid="see-free-match-score-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-[#03163d]" />
                <span>{isHi ? 'गणना की जा रही है...' : 'Calculating score...'}</span>
              </>
            ) : (
              <>
                <HeartHandshake className="w-4 h-4 text-[#ff9bb0]" />
                <span>{labels.seeFreeMatchScore}</span>
              </>
            )}
          </button>

          {/* Disabled Order Full Report Button */}
          <button
            type="button"
            disabled
            aria-disabled="true"
            className="w-full py-3 px-6 rounded-full bg-white/[0.04] border border-white/12 text-[#dcecff]/40 font-medium text-xs sm:text-sm cursor-not-allowed flex items-center justify-center gap-2 select-none"
            data-testid="order-report-disabled-btn"
          >
            <Lock className="w-3.5 h-3.5 text-[#dcecff]/30" />
            <span>{labels.orderFullDisabled}</span>
          </button>
        </div>

        {/* Privacy reassurance */}
        <p className="text-[11px] text-center text-[#dcecff]/60 font-['Outfit'] leading-relaxed pt-1">
          {labels.privacyNotice}
        </p>
      </form>
    </div>
  );
}
